import React, { useState } from "react";
import { useAuth, API } from "@/App";
import { Navigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle, X, ArrowRight, Star, Brain, Target, Zap, Mail, Lock, Eye, EyeOff, LineChart, Feather, Calculator, User, Search, Menu, Play, ChevronRight, Clock, Calendar } from "lucide-react";
import { motion } from "framer-motion";
import { supabase } from "@/supabaseClient";

function AuthModal({ onClose, initialMode = "login" }) {
  const { setUser } = useAuth();
  const [mode, setMode] = useState(initialMode); // login | signup | forgot
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  const exchangeSession = async (accessToken) => {
    const res = await axios.post(`${API}/auth/supabase-session`, { access_token: accessToken }, { withCredentials: true });
    return res.data;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password) { toast.error("Email and password are required"); return; }
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email: form.email.trim(), password: form.password });
      if (error) throw error;
      const user = await exchangeSession(data.session.access_token);
      setUser(user);
      toast.success("Welcome back!");
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Invalid email or password");
    } finally { setLoading(false); }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) { toast.error("All fields are required"); return; }
    if (form.password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signUp({
        email: form.email.trim(),
        password: form.password,
        options: { data: { name: form.name.trim() } },
      });
      if (error) throw error;
      if (data.session) {
        const user = await exchangeSession(data.session.access_token);
        setUser(user);
        toast.success("Account created! Welcome to MarketJournal.");
      } else {
        toast.success("Check your email to confirm your account, then sign in.");
        setMode("login");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Registration failed");
    } finally { setLoading(false); }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!form.email) { toast.error("Enter your email address"); return; }
    setLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(form.email.trim(), {
        redirectTo: window.location.origin + "/reset-password",
      });
      if (error) throw error;
      toast.success("Reset link sent! Check your email.");
      setMode("login");
    } catch (err) {
      toast.error(err.message || "Failed to send reset link");
    } finally { setLoading(false); }
  };

  const inputClass = "w-full h-11 rounded-xl text-sm pl-10 pr-4 bg-white/5 border border-white/10 text-white placeholder:text-zinc-600 outline-none focus:border-emerald-400/50 transition-colors";

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)" }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="liquid-glass animate-blur-fade-up w-full max-w-sm rounded-3xl p-7" style={{ background: "rgba(12,12,14,0.82)" }} data-testid="auth-modal">
        <div className="flex justify-end"><button onClick={onClose} data-testid="auth-close-btn" className="text-zinc-500 hover:text-white transition-colors"><X className="w-4 h-4" /></button></div>

        {/* ─── Login Mode ─── */}
        {mode === "login" && (
          <>
            <div className="text-center mb-5">
              <h2 className="text-2xl font-bold text-white tracking-tight">Welcome Back</h2>
              <p className="text-sm text-zinc-500 mt-1">Sign in to your account</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-3">
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
                <input data-testid="auth-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="Email" className={inputClass} />
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
                <input data-testid="auth-password" type={showPw ? "text" : "password"} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="Password" className={inputClass + " !pr-12"} autoComplete="current-password" />
                <button type="button" onClick={() => setShowPw(!showPw)}
                  className="absolute right-1 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white z-10 p-2 rounded-lg"
                  data-testid="auth-toggle-pw" tabIndex={-1} aria-label={showPw ? "Hide password" : "Show password"}>
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <div className="flex justify-end">
                <button type="button" onClick={() => setMode("forgot")} data-testid="forgot-password-link"
                  className="text-xs text-emerald-400 hover:text-emerald-300 font-medium">Forgot Password?</button>
              </div>
              <button data-testid="auth-submit" type="submit" disabled={loading}
                className="w-full h-11 rounded-xl text-sm font-semibold bg-emerald-400 text-black hover:bg-emerald-300 transition-colors disabled:opacity-50">
                {loading ? "Signing in..." : "Sign In"}
              </button>
            </form>

            <p className="text-center text-xs text-zinc-600 mt-4">
              New here? <button onClick={() => setMode("signup")} data-testid="switch-to-signup" className="text-emerald-400 hover:text-emerald-300 font-medium">Create an account</button>
            </p>
          </>
        )}

        {/* ─── Signup Mode ─── */}
        {mode === "signup" && (
          <>
            <div className="text-center mb-5">
              <h2 className="text-2xl font-bold text-white tracking-tight">Create Account</h2>
              <p className="text-sm text-zinc-500 mt-1">Start your trading journal</p>
            </div>

            <form onSubmit={handleSignup} className="space-y-3">
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
                <input data-testid="signup-name" type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Full Name" className={inputClass} />
              </div>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
                <input data-testid="signup-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="Email" className={inputClass} />
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
                <input data-testid="signup-password" type={showPw ? "text" : "password"} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="Password (min 6 characters)" className={inputClass + " !pr-12"} autoComplete="new-password" />
                <button type="button" onClick={() => setShowPw(!showPw)}
                  className="absolute right-1 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white z-10 p-2 rounded-lg" tabIndex={-1}
                  aria-label={showPw ? "Hide password" : "Show password"}>
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <button data-testid="signup-submit" type="submit" disabled={loading}
                className="w-full h-11 rounded-xl text-sm font-semibold bg-emerald-400 text-black hover:bg-emerald-300 transition-colors disabled:opacity-50">
                {loading ? "Creating account..." : "Create Account"}
              </button>
            </form>

            <p className="text-center text-xs text-zinc-600 mt-4">
              Already have an account? <button onClick={() => setMode("login")} data-testid="switch-to-login" className="text-emerald-400 hover:text-emerald-300 font-medium">Sign In</button>
            </p>
          </>
        )}

        {/* ─── Forgot Password Mode ─── */}
        {mode === "forgot" && (
          <>
            <div className="text-center mb-5">
              <h2 className="text-2xl font-bold text-white tracking-tight">Reset Password</h2>
              <p className="text-sm text-zinc-500 mt-1">We'll email you a secure reset link</p>
            </div>

            <form onSubmit={handleForgotPassword} className="space-y-3">
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
                <input data-testid="forgot-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="Enter your email" className={inputClass} />
              </div>
              <button data-testid="forgot-submit" type="submit" disabled={loading}
                className="w-full h-11 rounded-xl text-sm font-semibold bg-emerald-400 text-black hover:bg-emerald-300 transition-colors disabled:opacity-50">
                {loading ? "Sending..." : "Send Reset Link"}
              </button>
            </form>

            <p className="text-center text-xs text-zinc-600 mt-4">
              Remember your password? <button onClick={() => setMode("login")} className="text-emerald-400 hover:text-emerald-300 font-medium">Sign In</button>
            </p>
          </>
        )}
      </div>
    </div>
  );
}

export default function LandingPage() {
  const { user } = useAuth();
  const [authMode, setAuthMode] = useState(null); // null | 'login' | 'signup'
  const [menuOpen, setMenuOpen] = useState(false);
  const openAuth = (m) => { setAuthMode(m); setMenuOpen(false); };

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  const NAV_LINKS = [
    { label: "Features", href: "#features" },
    { label: "Pricing", href: "#pricing" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "Reviews", href: "#reviews" },
  ];

  const VIDEO_URL = "https://res.cloudinary.com/dfunddsen/video/upload/v1780376725/Automotive-Realty-Connect.pptx_1_f1thoe.mp4";

  return (
    <div className="relative min-h-screen bg-black overflow-x-hidden" data-testid="landing-page" style={{ fontFamily: "'Inter', sans-serif" }}>
      {/* SEO */}
      <div className="sr-only" aria-hidden="false">
        <h1>MarketJournal.In — Free AI-Powered Stock Market Trading Journal for Indian Traders</h1>
        <p>MarketJournal.In is India's smartest trading journal. Track your stock market trades on NSE and BSE, calculate profit and loss in INR, backtest strategies with a Paper Trade simulator using Rs 10,00,000 virtual capital, and build your personal trading knowledge base with Thread.</p>
      </div>

      {/* FIXED BACKGROUND VIDEO */}
      <video
        className="fixed inset-0 w-full h-full object-cover z-0"
        autoPlay loop muted playsInline preload="auto"
        data-testid="hero-video"
        poster=""
      >
        <source src={VIDEO_URL} type="video/mp4" />
      </video>

      {/* BOTTOM BLUR OVERLAY (no dark gradient) */}
      <div className="fixed inset-0 z-[1] pointer-events-none hero-bottom-blur" />

      {/* ───── CINEMATIC HERO (full viewport) ───── */}
      <section className="relative z-10 min-h-screen flex flex-col" aria-label="Hero">
        {/* NAVBAR */}
        <nav className="relative z-50 flex items-center justify-between px-4 sm:px-6 md:px-12 py-4 md:py-6" aria-label="Main navigation">
          <span className="text-xl md:text-2xl font-bold text-white tracking-tight animate-blur-fade-up" style={{ animationDelay: "0ms", fontFamily: "'Clash Display', sans-serif" }}>
            Market<span className="text-emerald-400">Journal</span>
          </span>

          <div className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map((l, i) => (
              <a key={l.href} href={l.href} className="text-sm text-white/80 hover:text-white transition-colors animate-blur-fade-up"
                style={{ animationDelay: `${100 + i * 50}ms` }}>{l.label}</a>
            ))}
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <button onClick={() => openAuth("login")} data-testid="login-button-nav"
              className="hidden sm:flex items-center gap-2 rounded-full liquid-glass text-white text-sm font-medium px-4 md:px-6 py-2 animate-blur-fade-up"
              style={{ animationDelay: "350ms" }}>
              <User size={18} /> Sign In
            </button>
            <button onClick={() => openAuth("signup")} data-testid="get-started-nav"
              className="hidden sm:block rounded-full bg-white text-black text-sm font-semibold px-4 md:px-6 py-2 hover:bg-gray-200 transition-colors animate-blur-fade-up"
              style={{ animationDelay: "400ms" }}>
              Get Started
            </button>
            <button onClick={() => setMenuOpen(!menuOpen)} data-testid="mobile-menu-toggle"
              className="lg:hidden w-10 h-10 rounded-full liquid-glass flex items-center justify-center text-white animate-blur-fade-up"
              style={{ animationDelay: "350ms" }} aria-label="Menu">
              <div className="relative w-5 h-5">
                <Menu size={20} className={`absolute inset-0 transition-all duration-500 ease-out ${menuOpen ? "rotate-180 opacity-0 scale-50" : "rotate-0 opacity-100 scale-100"}`} />
                <X size={20} className={`absolute inset-0 transition-all duration-500 ease-out ${menuOpen ? "rotate-0 opacity-100 scale-100" : "-rotate-180 opacity-0 scale-50"}`} />
              </div>
            </button>
          </div>
        </nav>

        {/* MOBILE MENU */}
        <div className={`lg:hidden absolute top-[72px] left-3 right-3 z-40 rounded-2xl bg-gray-900/95 backdrop-blur-lg border border-gray-800 shadow-2xl transition-all duration-500 ease-out ${menuOpen ? "translate-y-0 opacity-100" : "-translate-y-4 opacity-0 pointer-events-none"}`} data-testid="mobile-menu">
          <div className="flex flex-col p-3">
            {NAV_LINKS.map((l, i) => (
              <a key={l.href} href={l.href} onClick={() => setMenuOpen(false)}
                className={`py-3 px-3 rounded-lg text-white/90 hover:bg-gray-800/50 transition-all duration-300 ${menuOpen ? "translate-x-0 opacity-100" : "translate-x-3 opacity-0"}`}
                style={{ transitionDelay: `${i * 50}ms` }}>{l.label}</a>
            ))}
            <div className="sm:hidden flex gap-2 mt-2 pt-3 border-t border-gray-800">
              <button onClick={() => openAuth("login")} data-testid="mobile-signin" className="flex-1 rounded-full liquid-glass text-white text-sm font-medium py-2.5">Sign In</button>
              <button onClick={() => openAuth("signup")} data-testid="mobile-getstarted" className="flex-1 rounded-full bg-white text-black text-sm font-semibold py-2.5">Get Started</button>
            </div>
          </div>
        </div>

        {/* HERO CONTENT (bottom) */}
        <div className="flex-1 flex flex-col justify-end px-4 sm:px-6 md:px-12 pb-10 md:pb-16 relative z-10">
          <div className="flex flex-col md:flex-row items-start md:items-end gap-8">
            {/* Left */}
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-3 sm:gap-6 mb-6 md:mb-8 text-xs sm:text-sm text-white animate-blur-fade-up" style={{ animationDelay: "300ms" }}>
                <span className="flex items-center gap-1.5"><Star className="w-4 h-4 sm:w-5 sm:h-5 fill-white text-white" /> <span className="font-medium">4.9/5 Trader Rating</span></span>
                <span className="flex items-center gap-1.5"><Clock className="w-4 h-4 sm:w-5 sm:h-5" /> 5-min daily journaling</span>
                <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4 sm:w-5 sm:h-5" /> 7-Day Free Trial</span>
              </div>

              <h2 className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-normal text-white mb-4 md:mb-6 animate-blur-fade-up"
                style={{ animationDelay: "400ms", letterSpacing: "-0.04em", fontFamily: "'Clash Display', sans-serif" }}>
                Get Your Best Out<br />Of The Market.
              </h2>

              <p className="text-base sm:text-lg md:text-xl text-gray-300 mb-6 md:mb-12 max-w-2xl animate-blur-fade-up font-light" style={{ animationDelay: "500ms" }}>
                An AI-powered trading journal that turns every trade into a lesson — find your edge, build discipline, and grow in the world of the stock market.
              </p>

              <div className="flex flex-wrap items-center gap-3 sm:gap-4">
                <button onClick={() => openAuth("signup")} data-testid="hero-get-started-btn"
                  className="flex items-center gap-2 bg-white text-black rounded-full font-medium px-6 sm:px-8 py-2.5 sm:py-3 hover:bg-gray-200 transition-colors animate-blur-fade-up"
                  style={{ animationDelay: "600ms" }}>
                  <Play size={18} className="fill-black" /> Get Started
                </button>
                <button onClick={() => openAuth("login")} data-testid="hero-signin-btn"
                  className="rounded-full font-medium liquid-glass text-white px-6 sm:px-8 py-2.5 sm:py-3 animate-blur-fade-up"
                  style={{ animationDelay: "700ms" }}>
                  Sign In
                </button>
              </div>
            </div>

            {/* Right: section shortcuts (pills) */}
            <div className="flex md:flex-col gap-3 md:items-end">
              <a href="#features" data-testid="hero-features-link"
                className="flex items-center gap-2 rounded-full liquid-glass text-white text-sm font-medium px-4 sm:px-6 py-2.5 sm:py-3 animate-blur-fade-up"
                style={{ animationDelay: "800ms" }}>
                Explore Features <ChevronRight size={18} />
              </a>
              <a href="#pricing" data-testid="hero-pricing-link"
                className="flex items-center gap-2 rounded-full liquid-glass text-white text-sm font-medium px-4 sm:px-6 py-2.5 sm:py-3 animate-blur-fade-up"
                style={{ animationDelay: "900ms" }}>
                See Pricing <ChevronRight size={18} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ───── SECTIONS BELOW (cinematic glass over the same video) ───── */}
      <main className="relative z-10 bg-black/65">
        {/* PROBLEM / SOLUTION */}
        <section className="w-full" aria-label="Why MarketJournal">
          <div className="grid md:grid-cols-2">
            <motion.div className="bg-white/[0.03] backdrop-blur-lg p-10 md:p-16"
              initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.6 }}>
              <span className="text-[10px] tracking-[0.2em] uppercase text-zinc-600 font-bold">THE OLD WAY</span>
              <h2 className="text-4xl md:text-5xl font-normal text-white mt-4 mb-8 tracking-tight leading-[1.05]" style={{fontFamily: "'Clash Display', sans-serif", letterSpacing: "-0.04em"}}>Random<br />Trading</h2>
              <div className="space-y-4">
                {["No structured framework — guessing entries and exits", "No idea which strategies actually work for you", "Emotional decisions destroying your capital", "No trade journal — repeating the same mistakes", "No risk-free way to test new strategies"].map((item, i) => (
                  <motion.div key={i} className="flex items-start gap-3" initial={{ opacity: 0, x: -10 }} whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }} transition={{ delay: i * 0.1, duration: 0.4 }}>
                    <X className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <span className="text-zinc-500 text-sm font-light leading-relaxed">{item}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div className="bg-emerald-400/[0.04] backdrop-blur-lg p-10 md:p-16 border-l border-emerald-400/20"
              initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.6 }}>
              <span className="text-[10px] tracking-[0.2em] uppercase text-emerald-400/60 font-bold">THE MARKETJOURNAL WAY</span>
              <h2 className="text-4xl md:text-5xl font-normal text-white mt-4 mb-8 tracking-tight leading-[1.05]" style={{fontFamily: "'Clash Display', sans-serif", letterSpacing: "-0.04em"}}>Structured<br /><span className="text-emerald-400">Edge</span></h2>
              <div className="space-y-4">
                {["Paper Trade simulator — backtest strategies risk-free", "AI identifies your highest-profit patterns and biases", "Mandatory trade journals build disciplined reflection", "Thread captures your evolving market knowledge", "Position sizing calculator ensures proper risk management"].map((item, i) => (
                  <motion.div key={i} className="flex items-start gap-3" initial={{ opacity: 0, x: 10 }} whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }} transition={{ delay: i * 0.1, duration: 0.4 }}>
                    <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                    <span className="text-zinc-300 text-sm font-light leading-relaxed">{item}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* FEATURES BENTO GRID */}
        <section id="features" className="max-w-7xl mx-auto px-6 py-24" aria-label="Features">
          <motion.div className="text-center mb-16" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.6 }}>
            <span className="text-[10px] tracking-[0.2em] uppercase text-zinc-600 font-bold">FEATURES</span>
            <h2 className="text-5xl md:text-6xl font-normal text-white mt-3 tracking-tight leading-[1.05]" style={{fontFamily: "'Clash Display', sans-serif", letterSpacing: "-0.04em"}}>
              Your Edge,<br /><span className="text-emerald-400">Engineered</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:auto-rows-[320px]">
            {[
              { span: "md:col-span-2", icon: <LineChart className="w-8 h-8 text-emerald-400" />, title: "Paper Trade Simulator", desc: "Backtest strategies with Rs 10,00,000 virtual capital. Market & limit orders with realistic slippage.", extra: (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4">
                  {[{ l: "Virtual Capital", v: "Rs 10L", c: "#34D399" }, { l: "Win Rate", v: "62%", c: "#fff" }, { l: "Sharpe Ratio", v: "1.84", c: "#34D399" }, { l: "Total Return", v: "+8.4%", c: "#34D399" }].map((m, i) => (
                    <div key={i} className="bg-white/5 rounded-xl p-3 border border-white/5"><span className="text-[9px] text-zinc-600 uppercase tracking-wider font-bold">{m.l}</span><p className="font-mono text-lg font-bold mt-1" style={{color: m.c}}>{m.v}</p></div>
                  ))}
                </div>
              )},
              { icon: <Feather className="w-7 h-7 text-emerald-400" />, title: "Thread — Your Second Brain", desc: "Write down what you learn. AI connects the dots and shows how your thinking evolves over time." },
              { icon: <Calculator className="w-7 h-7 text-zinc-400" />, title: "Position Sizing Calculator", desc: "Professional-grade sizing: Capital x Risk% / (Entry - SL). Auto-caps allocation.", extra: (
                <div className="bg-white/5 rounded-xl p-3 border border-white/5 mt-4"><span className="text-[9px] text-zinc-600 uppercase tracking-wider font-bold">Formula</span><p className="font-mono text-xs text-emerald-400 mt-1">(Capital x Risk%) / (Entry - SL)</p></div>
              )},
              { span: "md:col-span-2", icon: <Brain className="w-7 h-7 text-zinc-400" />, title: "AI Behavioral Analysis", desc: "Claude AI analyzes your last 20 trades. Identifies FOMO, revenge trades, and round-number biases with specific examples.", extra: (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4">
                  {[{ l: "Total P&L", v: "+1,84,500", c: "#34D399" }, { l: "Win Rate", v: "62%", c: "#fff" }, { l: "Avg Gain", v: "+4,230", c: "#34D399" }, { l: "Sharpe", v: "2.14", c: "#fff" }].map((m, i) => (
                    <div key={i} className="bg-white/5 rounded-xl p-3 border border-white/5"><span className="text-[9px] text-zinc-600 uppercase tracking-wider font-bold">{m.l}</span><p className="font-mono text-lg font-bold mt-1" style={{color: m.c}}>{m.v}</p></div>
                  ))}
                </div>
              )},
              { icon: <Zap className="w-7 h-7 text-emerald-400" />, title: "Trade Journal", desc: "Required journal on every trade close — thesis, outcome, and lesson.", extra: (
                <div className="flex gap-2 mt-4">
                  {['Thesis', 'Outcome', 'Lesson'].map((e, i) => (
                    <div key={i} className="flex-1 rounded-lg p-2 text-center border border-emerald-400/20 bg-emerald-400/10 text-emerald-400"><span className="text-xs font-semibold">{e}</span></div>
                  ))}
                </div>
              )},
              { icon: <Target className="w-7 h-7 text-zinc-400" />, title: "Custom Setups", desc: "Define your own trading setups. Track which setups generate the most profit.", extra: (
                <div className="grid grid-cols-3 gap-2 mt-4">
                  {['Breakout', 'Reversion', 'Momentum'].map((s, i) => (
                    <span key={i} className="text-[10px] text-zinc-500 font-bold uppercase tracking-wider bg-white/5 border border-white/5 px-3 py-1.5 rounded-lg text-center">{s}</span>
                  ))}
                </div>
              )},
            ].map((f, idx) => (
              <motion.div key={idx} className={`${f.span || ''} bg-white/[0.04] backdrop-blur-xl border border-white/10 rounded-2xl p-6 md:p-8 flex flex-col justify-between overflow-hidden hover:-translate-y-1 hover:border-emerald-400/30 hover:shadow-lg hover:shadow-emerald-400/5 transition-all duration-300`}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }} transition={{ delay: idx * 0.08, duration: 0.5 }}>
                <div>{f.icon}<h3 className="text-xl font-semibold text-white mb-2 mt-4 tracking-tight" style={{fontFamily: "'Clash Display', sans-serif", letterSpacing: "-0.04em"}}>{f.title}</h3><p className="text-zinc-500 text-sm font-light leading-relaxed max-w-md">{f.desc}</p></div>
                {f.extra}
              </motion.div>
            ))}
          </div>
        </section>

        {/* PRICING */}
        <section id="pricing" className="max-w-7xl mx-auto px-6 py-24" aria-label="Pricing">
          <motion.div className="text-center mb-12" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <h2 className="text-4xl sm:text-5xl font-normal text-white tracking-tight mb-4" style={{fontFamily: "'Clash Display', sans-serif", letterSpacing: "-0.04em"}}>Simple Pricing</h2>
            <p className="text-base text-zinc-500 font-light max-w-xl mx-auto">Start free for 7 days. No credit card required.</p>
          </motion.div>
          <motion.div className="max-w-md mx-auto" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ delay: 0.2, duration: 0.6 }}>
            <div className="bg-white/[0.05] backdrop-blur-2xl rounded-2xl p-8 border border-emerald-400/30 relative overflow-hidden hover:border-emerald-400/50 transition-colors duration-300" data-testid="pricing-card-landing">
              <div className="absolute top-0 right-0 bg-emerald-400 text-black text-[10px] font-bold px-4 py-1.5 rounded-bl-xl tracking-wider">7 DAYS FREE</div>
              <div className="absolute -top-20 -right-20 w-40 h-40 bg-emerald-400/5 rounded-full blur-3xl" />
              <div className="mb-6 relative"><h3 className="text-xl font-bold text-white mb-1" style={{fontFamily: "'Clash Display', sans-serif", letterSpacing: "-0.04em"}}>Premium</h3><p className="text-xs text-zinc-500">Everything you need to trade profitably</p></div>
              <div className="flex items-baseline gap-1 mb-6"><span className="text-5xl font-bold text-emerald-400" style={{fontFamily: "'Clash Display', sans-serif", letterSpacing: "-0.04em"}}>Rs.4,999</span><span className="text-sm text-zinc-500 font-light">/year</span></div>
              <div className="space-y-3 mb-8">
                {["Unlimited trade logging with AI analysis", "Paper Trading with Rs.10L virtual capital", "Thread — AI-powered second brain", "Position sizing & risk calculator", "Weekly AI reports in simple language", "Discipline score & reality gap tracking", "PDF exports & trade history"].map((f) => (
                  <div key={f} className="flex items-start gap-3"><CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" /><span className="text-sm text-zinc-400">{f}</span></div>
                ))}
              </div>
              <button onClick={() => openAuth("signup")} data-testid="pricing-cta-btn"
                className="w-full text-base font-semibold bg-emerald-400 text-black py-4 rounded-xl hover:bg-emerald-300 active:scale-[0.98] transition-all duration-200">
                Start 7-Day Free Trial
              </button>
              <p className="text-center text-[10px] text-zinc-600 mt-3">No credit card required to start</p>
            </div>
          </motion.div>
        </section>

        {/* HOW IT WORKS */}
        <section id="how-it-works" className="border-y border-white/5">
          <div className="max-w-7xl mx-auto px-6 py-24">
            <div className="grid md:grid-cols-3 gap-16 md:gap-8">
              <motion.div className="md:sticky md:top-32 md:self-start" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }}
                viewport={{ once: true }} transition={{ duration: 0.6 }}>
                <span className="text-[10px] tracking-[0.2em] uppercase text-zinc-600 font-bold">PROCESS</span>
                <h2 className="text-5xl md:text-7xl font-normal text-white mt-3 tracking-tight leading-[1.05]" style={{fontFamily: "'Clash Display', sans-serif", letterSpacing: "-0.04em"}}>How It<br /><span className="text-emerald-400">Works</span></h2>
              </motion.div>
              <div className="md:col-span-2 space-y-12">
                {[
                  { num: "01", title: "LOG & BACKTEST", desc: "Enter trades manually or backtest past strategies with Paper Trade. Every position close requires a journal." },
                  { num: "02", title: "AI ANALYZES", desc: "Claude AI analyzes your journal entries and identifies recurring behavioral patterns — FOMO exits, round-number biases." },
                  { num: "03", title: "SCALE YOUR EDGE", desc: "Performance dashboards show P&L, Sharpe ratio, win rate. Thread captures your evolving market knowledge." },
                ].map((step, i) => (
                  <motion.div key={i} className="group flex gap-6 items-start" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }} transition={{ delay: i * 0.15, duration: 0.5 }}>
                    <span className="text-7xl md:text-8xl font-bold text-white/[0.08] group-hover:text-emerald-400/25 transition-colors duration-500 leading-none select-none" style={{fontFamily: "'Clash Display', sans-serif", letterSpacing: "-0.04em"}}>{step.num}</span>
                    <div className="pt-4">
                      <h3 className="text-xl md:text-2xl font-semibold text-white mb-2 tracking-tight" style={{fontFamily: "'Clash Display', sans-serif", letterSpacing: "-0.04em"}}>{step.title}</h3>
                      <p className="text-zinc-500 text-sm font-light leading-relaxed max-w-md">{step.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* TESTIMONIALS */}
        <section id="reviews" className="max-w-7xl mx-auto px-6 py-24">
          <motion.div className="text-center mb-16" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <span className="text-[10px] tracking-[0.2em] uppercase text-zinc-600 font-bold">TRADERS LOVE US</span>
            <h2 className="text-5xl md:text-6xl font-normal text-white mt-3 tracking-tight leading-[1.05]" style={{fontFamily: "'Clash Display', sans-serif", letterSpacing: "-0.04em"}}>What They Say</h2>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { name: "Rahul S.", role: "Swing Trader, Mumbai", text: "Finally a journal that understands structure. The setup classification changed how I think about entries. My win rate jumped from 40% to 58%." },
              { name: "Priya M.", role: "Full-time Trader, Bangalore", text: "The AI weekly report is like having a mentor. It caught my FOMO pattern on Monday mornings — once I fixed that, my losses dropped 40%." },
              { name: "Arjun K.", role: "Part-time Trader, Delhi", text: "CSV import saved me hours. I migrated 200 trades and got a complete edge analysis in 2 minutes. The expectancy engine is incredible." },
            ].map((t, i) => (
              <motion.div key={i} className={`bg-white/[0.04] backdrop-blur-xl border border-white/10 rounded-2xl p-8 hover:-translate-y-1 hover:border-emerald-400/30 transition-all duration-300 ${i === 1 ? 'md:translate-y-4' : ''}`}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.15, duration: 0.5 }}>
                <div className="flex gap-1 mb-4">
                  {[1,2,3,4,5].map(s => <Star key={s} className="w-4 h-4 fill-emerald-400 text-emerald-400" />)}
                </div>
                <p className="text-zinc-300 text-sm font-light leading-relaxed mb-6">"{t.text}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-emerald-400/10 flex items-center justify-center">
                    <span className="text-sm font-semibold text-emerald-400">{t.name[0]}</span>
                  </div>
                  <div><p className="text-sm font-medium text-white">{t.name}</p><p className="text-xs text-zinc-600">{t.role}</p></div>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="relative overflow-hidden" aria-label="CTA">
          <div className="absolute inset-0 bg-gradient-to-t from-emerald-400/5 to-transparent" />
          <motion.div className="max-w-4xl mx-auto px-6 py-24 text-center relative z-10"
            initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
            <h2 className="text-5xl md:text-7xl lg:text-8xl font-normal text-white tracking-tight leading-[1.05] mb-6" style={{fontFamily: "'Clash Display', sans-serif", letterSpacing: "-0.04em"}}>
              Find Your<br /><span className="text-emerald-400">Structural Edge</span>
            </h2>
            <p className="text-base sm:text-lg text-zinc-500 font-light max-w-2xl mx-auto mb-10 leading-relaxed">
              Stop guessing. Start journaling with structure. Let AI show you what's actually making — and losing — you money.
            </p>
            <button onClick={() => openAuth("signup")} data-testid="cta-bottom-btn"
              className="text-base font-semibold bg-emerald-400 text-black px-10 py-5 rounded-full hover:bg-emerald-300 hover:scale-105 active:scale-[0.98] transition-all duration-300 shadow-[0_0_40px_rgba(52,211,153,0.15)]">
              Get Started Free <ArrowRight className="inline w-5 h-5 ml-2" />
            </button>
          </motion.div>
        </section>
      </main>

      {/* FOOTER */}
      <footer className="relative z-10 border-t border-white/10 py-8 bg-black/50 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <span className="text-lg font-bold text-white tracking-tight" style={{fontFamily: "'Clash Display', sans-serif", letterSpacing: "-0.04em"}}>Market<span className="text-emerald-400">Journal</span><span className="text-zinc-500 text-sm font-light">.In</span></span>
          <span className="text-xs text-zinc-600 font-light">India's smartest AI-powered stock market trading journal.</span>
        </div>
      </footer>
      {authMode && <AuthModal initialMode={authMode} onClose={() => setAuthMode(null)} />}
    </div>
  );
}
